import React, { useMemo, useState } from 'react';
import DefinitionSection from '../common/DefinitionSection';
import AnsweredGridPreview from '../shared/AnsweredGridPreview';
import ZoomedImagePreview from '../shared/ZoomedImagePreview';
import DailyCountdown from './DailyCountdown';
import DailyPrivateShareCardModal from './DailyPrivateShareCardModal';
import DailyLockedStatsPreview from './DailyLockedStatsPreview';
import RewardSummary from '../shared/RewardSummary';
import { getDailyGuessBucketNumbers } from '../../../utils/games/dailyGuessBuckets';
import './DailyGameModal.css';

const DailyGameModal = ({
  title,
  isWin,
  user = null,
  stats = null,
  solution,
  imageUrl,
  puzzleDateUtc,
  shareZoomLevel,
  shareZoomOrigin,
  guesses,
  secondsUntilRollover,
  showDefinition,
  definition,
  isLoadingDefinition,
  toggleDefinition,
  pointsAwarded = 0,
  firstGameBonus = false,
  onHome,
}) => {
  const [showPrivateShareModal, setShowPrivateShareModal] = useState(false);
  const guessBucketNumbers = useMemo(() => getDailyGuessBucketNumbers(), []);
  const safeStats = useMemo(
    () => ({
      guesses: guessBucketNumbers.reduce((guessesByBucket, guessNumber) => {
        guessesByBucket[guessNumber] = Math.max(0, Number(stats?.guesses?.[guessNumber]) || 0);
        return guessesByBucket;
      }, {}),
      losses: Math.max(0, Number(stats?.losses) || 0),
      currentStreak: Math.max(0, Number(stats?.currentStreak) || 0),
      maxStreak: Math.max(0, Number(stats?.maxStreak) || 0),
    }),
    [guessBucketNumbers, stats],
  );
  const wins = Object.values(safeStats.guesses).reduce((total, count) => total + count, 0);
  const totalGames = wins + safeStats.losses;
  const winPercentage = totalGames > 0 ? Math.round((wins / totalGames) * 100) : 0;
  const maxGuesses = Math.max(...Object.values(safeStats.guesses));

  if (showPrivateShareModal) {
    return (
      <DailyPrivateShareCardModal
        imageUrl={imageUrl}
        puzzleDateUtc={puzzleDateUtc}
        shareZoomLevel={shareZoomLevel}
        shareZoomOrigin={shareZoomOrigin}
        guesses={guesses}
        solution={solution}
        onClose={() => setShowPrivateShareModal(false)}
        onHome={onHome}
      />
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content game-result-modal game-result-modal--scrollbar">
        <h2>{title}</h2>
        <RewardSummary
          pointsAwarded={pointsAwarded}
          firstGameBonus={firstGameBonus}
        />
        <div className="modal-image-container game-result-modal__media">
          <ZoomedImagePreview
            imageUrl={imageUrl}
            alt={solution || 'Daily puzzle'}
            zoomLevel={100}
            zoomOrigin={null}
            size={240}
            borderRadius={10}
          />

          {solution ? (
            <p>
              Word: <strong>{solution.toUpperCase()}</strong>
            </p>
          ) : null}

          <p>
            {isWin
              ? 'Great work. Come back tomorrow for a fresh daily puzzle.'
              : 'Come back tomorrow for a fresh daily puzzle.'}
          </p>

          <button
            type="button"
            className="definition-button daily-game-modal__share-button"
            onClick={() => setShowPrivateShareModal(true)}
            disabled={!imageUrl || !solution}
          >
            <span className="daily-game-modal__share-icon" aria-hidden="true">
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 16V4M12 4L7 9M12 4L17 9"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6 14V18C6 18.5304 6.21071 19.0391 6.58579 19.4142C6.96086 19.7893 7.46957 20 8 20H16C16.5304 20 17.0391 19.7893 17.4142 19.4142C17.7893 19.0391 18 18.5304 18 18V14"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span>Share without revealing answers</span>
          </button>

          <DefinitionSection
            word={solution}
            showDefinition={showDefinition}
            definition={definition}
            isLoadingDefinition={isLoadingDefinition}
            toggleDefinition={toggleDefinition}
          />
          <AnsweredGridPreview guesses={guesses} solution={solution} />
        </div>

        {user ? (
          <div className="stats-container">
            <div className="stats-summary">
              <div className="stat-box">
                <span className="stat-number">{safeStats.currentStreak}</span>
                <span className="stat-label">Streak</span>
              </div>
              <div className="stat-box">
                <span className="stat-number">{safeStats.maxStreak}</span>
                <span className="stat-label">Max Streak</span>
              </div>
              <div className="stat-box">
                <span className="stat-number">{totalGames}</span>
                <span className="stat-label">Played</span>
              </div>
              <div className="stat-box">
                <span className="stat-number">{winPercentage}</span>
                <span className="stat-label">Win %</span>
              </div>
            </div>
            <div className="guess-distribution">
              <h3>Guess Distribution</h3>
              {guessBucketNumbers.map((guessNumber) => {
                const percentage = maxGuesses
                  ? (safeStats.guesses[guessNumber] / maxGuesses) * 90
                  : 0;
                return (
                  <div key={guessNumber} className="guess-row">
                    <div className="guess-label">{guessNumber}</div>
                    <div className="guess-bar-container">
                      <div
                        className="guess-bar"
                        style={{ width: `${Math.min(90, percentage)}%` }}
                      >
                        {safeStats.guesses[guessNumber] || 0}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <DailyLockedStatsPreview guessBucketNumbers={guessBucketNumbers} />
        )}

        <div className="daily-game-modal__countdown">
          <DailyCountdown
            secondsUntilRollover={secondsUntilRollover}
            labelPrefix="Next daily countdown:"
          />
        </div>

        <button type="button" className="modal-button" onClick={onHome}>
          Home
        </button>
      </div>
    </div>
  );
};

export default DailyGameModal;

